<template>
  <div class="w-full" id="WhereYouLive">
    <ValidationObserver tag="form" ref="WhereYouLive" v-slot="observer">
      <h2 data-prefix="2" class="mb-2 text-4xl font-semibold leading-7">Where you live</h2>
      <p class="text-base">We use your address history to confirm your identity and to match you with information about your credit history.</p>
      <div class="mt-5 mb-6">

        <ValidationProvider name="UK Resident selection" rules="required">
            <div slot-scope="{ errors }">

              <!-- Are you a UK Resident? -->
              <p class="font-semibold label" for="residentialStatusCodeYes">Are you a UK Resident?</p>

              <div class="flex">
        
              <label class="mr-4 checkbox-label">
                <div :class="{ 'border-error': errors[0], 'bg-polgrey-dark border-polgrey-dark' : ukResidencyCode == 'Y', }" class="flex inline-flex items-center justify-center w-6 h-6 p-1 mr-4 text-lg bg-white border rounded cursor-pointer border-polgrey-medium">
                  <input tabindex="0" name="ukResidencyCode" :aria-checked="`${ukResidencyCode == 'Y'}`" value="Y" v-model="ukResidencyCode" true-value="Y" false-value="N" type="radio" class="hidden">
                  <TickIcon class="hidden w-6 h-6 pointer-events-none fill-current"/>
                </div>
                <div class="relative inline mr-2 select-none check-label"> Yes</div>
              </label>

              <label class="checkbox-label">
                <div :class="{ 'border-error': errors[0], 'bg-polgrey-dark border-polgrey-dark' : ukResidencyCode == 'N', }" class="flex inline-flex items-center justify-center w-6 h-6 p-1 mr-4 text-lg bg-white border rounded cursor-pointer border-polgrey-medium">
                  <input tabindex="0" name="ukResidencyCode" :aria-checked="`${ukResidencyCode == 'N'}`" value="N" v-model="ukResidencyCode" true-value="Y" false-value="N" type="radio" class="hidden">
                  <TickIcon class="hidden w-6 h-6 pointer-events-none fill-current"/>
                </div>
                <div class="relative inline mr-2 select-none check-label"> No</div>
              </label>

              </div>
              <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
            </div>
          </ValidationProvider>

      </div>
      <div class="mt-5 mb-6">

        <ValidationProvider name="Residential Status selection" rules="required">
            <div slot-scope="{ errors }">
        
              <p class="font-semibold label">Residential Status</p>
              
              <!-- Residential Status -->
              <fieldset>
                <legend class="font-semibold label">Residential Status</legend>
                <div class="inline" v-for="residentialStatus in residentialStatusCodes" :key="residentialStatus">
                    <input tabindex="0" v-model="residentialStatusCode" type="radio" :id="lower(prettifyResidentialStatus(residentialStatus))" :value="residentialStatus" />
                    <label @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" :for="lower(prettifyResidentialStatus(residentialStatus))">
                      {{ prettifyResidentialStatus(residentialStatus) }}
                      <TickIcon class="tick-icon"/>
                    </label>
                </div>
              </fieldset>

              <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>

            </div>
          </ValidationProvider>

      </div>
      <h2 :class="{
        'text-polred': toggles.highlightMissingAddress
      }" class="mt-5 mb-2 text-2xl font-semibold">Current address</h2>
      <div v-if="toggles.showAddressEntry && currentAddress">
        <div class="flex">
          <div class="w-3/4 lg:w-2/4">
            <h6 class="font-semibold">Address</h6>
            <template class="" v-for="(value, key, index) in currentAddress">
              <span :key="index" v-if="!['yearsAtAddress', 'monthsAtAddress'].includes(key)">{{ value }}<span :key="index" v-if="index !== 3">, </span>
              </span>
            </template>
            <h6 class="mt-4 font-semibold">Time at address</h6>
            {{ currentAddress.yearsAtAddress }} years, {{ currentAddress.monthsAtAddress }} months
          </div>
          <div class="w-1/4 mt-4">
            <button type="button" @click="changeCurrentAddress()" class="block px-4 py-2 underline">
              Change
            </button>
            <button type="button" @click="cancelCurrentTimeAtAddress()" class="block px-4 py-2 mt-10 underline lg:mt-6">
              Change
            </button>
          </div>
        </div>
        <div v-if="toggles.changeCurrentTimeAtAddress">
          <h3 class="mt-5 text-2xl font-semibold">Change time at address</h3>
            <div class="flex mt-4 mb-4 lg:mb-0">
              <div class="w-2/4 lg:w-1/4">
                <label class="font-semibold label" for="years">Years</label>
                <time-at-address :max="99" :model.sync="currentTimeAtAddress.years"/>
              </div>
              <div class="w-2/4 ml-6 lg:w-1/4">
                <label class="font-semibold label" for="years">Months</label>
                <time-at-address :max="11" :model.sync="currentTimeAtAddress.months"/>
              </div>
            </div>
            <div class="mt-4 mb-4">
              <button type="button" @click="saveCurrentTimeAtAddress()" class="px-4 py-2 mt-4 font-semibold text-white rounded-full shadow hover:bg-polred-dark bg-polred">
                Save Address
              </button>
              <button type="button" @click="cancelCurrentTimeAtAddress()" class="px-0 md:px-4 py-2 ml-3 font-semibold underline">
                Cancel
              </button>
            </div>
          </div>
        </div>
      <div v-else>
        <AddressSearch id="current"></AddressSearch>
      </div>

      <!-- Previous Address Required -->
      <template v-if="toggles.previousAddressRequired && toggles.showPreviousAddressEntry">
        
        <hr class="mt-4 mb-2"/>
        <h2 class="mt-5 text-3xl font-semibold">Previous address</h2>
        <p class="mb-4 text-m">Since you have lived at your current address less than two years, we need your previous address to complete your address history.</p>
        
        <div v-if="previousAddress">
          <div class="flex">
            <div class="w-2/4">
              <h6 class="font-semibold">Address</h6>
              <template v-for="(value, key, index) in previousAddress">
                <span :key="index" v-if="!['yearsAtAddress', 'monthsAtAddress'].includes(key)">{{ value }}<span :key="index" v-if="index !== 3">, </span>
                </span>
              </template>
              <h6 class="mt-4 font-semibold">Time at address</h6>
              {{ previousAddress.yearsAtAddress }} years, {{ previousAddress.monthsAtAddress }} months
            </div>
            <div class="w-1/4 mt-4">
              <button type="button" @click="toggles.showPreviousAddressEntry = true, toggles.changePreviousTimeAtAddress = false" class="block px-4 py-2 underline">
                Change
              </button>
              <button type="button" @click="toggles.changePreviousTimeAtAddress = true" class="block px-4 py-2 mt-6 underline">
                Change
              </button>
            </div>
            </div>
          </div>
        
        <div v-if="currentAddress.yearsAtAddress < 2 && toggles.showPreviousAddressEntry">
          <AddressSearch id="previous" />
        </div>

        <div v-if="toggles.changePreviousTimeAtAddress">
          <h3 class="mt-5 text-2xl font-semibold">Change time at address</h3>
          <div class="flex mt-4">
            <div class="w-1/4">
            <label class="font-semibold label" for="years">Years</label>
              <time-at-address :max="99" :model.sync="previousTimeAtAddress.years"/>
            </div>
            <div class="w-1/4 ml-6">
            <label class="font-semibold label" for="years">Months</label>
              <time-at-address :min="previousTimeAtAddress.years > 0 ? 0 : 1" :max="11" :model.sync="previousTimeAtAddress.months"/>
            </div>
          </div>
          <button type="button" @click="savePreviousTimeAtAddress()" class="px-4 py-2 mt-4 font-semibold text-white rounded-full shadow bg-polred hover:bg-polred-dark">
            Save Address
          </button>
          <button type="button" @click="toggles.changePreviousTimeAtAddress = false" class="px-0 md:px-4 py-2 ml-3 font-semibold underline">
            Cancel
          </button>
        </div>
      </template>
    </ValidationObserver>
  </div>
</template>

<script>

import TickIcon from '@/v2/assets/images/icons/select-tick.svg';

import { mapFields } from 'vuex-map-fields'
import mixins from '@/v2/mixins'
import { residentialStatusCodes } from '../../../form'

import AddressSearch from '@/v2/components/elements/AddressSearch'
import TimeAtAddress from '@/v2/components/elements/TimeAtAddress'

export default {
  name: 'AboutYourself',
  mixins: [mixins],
  computed : {
    ...mapFields([
      'person.residentialStatusCode',
      'person.ukResidencyCode',
      'person.currentAddress',
      'person.previousAddress',
    ])
  },
  methods: {
    saveCurrentTimeAtAddress() {

        this.toggles.changeCurrentTimeAtAddress = false
        this.currentAddress.monthsAtAddress = this.currentTimeAtAddress.months
        this.currentAddress.yearsAtAddress = this.currentTimeAtAddress.years

        if (this.currentAddress.yearsAtAddress >= parseInt(process.env.VUE_APP_ADDRESS_MIN_YEARS)) {
            this.previousAddress = null
            this.toggles.previousAddressRequired = false
            this.toggles.showPreviousAddressEntry = false
            this.$root.$emit('closeEditFields')
            return
        }

        this.toggles.previousAddressRequired = true
        this.toggles.showPreviousAddressEntry = true
    },
  },
  components: {
    AddressSearch,
    TimeAtAddress,
    TickIcon
  },
  mounted() {
    this.$root.$on('AddressSearch:set', (address) => { this.setSelectedAddress(address) })
    this.toggles.showAddressEntry = true
    
    if (this.currentAddress && this.currentAddress.yearsAtAddress !== null && this.currentAddress.monthsAtAddress !== null) {
      this.currentTimeAtAddress.years = this.currentAddress.yearsAtAddress
      this.currentTimeAtAddress.months = this.currentAddress.monthsAtAddress
    }

    if (this.previousAddress && this.previousAddress.yearsAtAddress !== null && this.previousAddress.monthsAtAddress !== null) {
      this.previousTimeAtAddress.years = this.previousAddress.yearsAtAddress
      this.previousTimeAtAddress.months = this.previousAddress.monthsAtAddress
    }
  },
  data() {
    return {
      residentialStatusCodes,
      toggles: {
        showAddressEntry: false,
        showPreviousAddressEntry: false,
        previousAddressRequired: false,
        changePreviousTimeAtAddress: false,
        changeCurrentTimeAtAddress: false,
        highlightMissingAddress: false,
        currentTimeAtAddress: {
          years: 0,
          months: 0
        },
        previousTimeAtAddress: {
          years: 0,
          months: 0
        }
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
